import { APP_INITIALIZER } from '@angular/core';
import { NgModule, ModuleWithProviders, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';

import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';

import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule as KendoButtonsModule } from '@progress/kendo-angular-buttons';

import { FlotDirective } from './directives/flot/flot.directive';
import { SparklineDirective } from './directives/sparkline/sparkline.directive';
import { EasypiechartDirective } from './directives/easypiechart/easypiechart.directive';
import { ColorsService } from './colors/colors.service';
import { CheckallDirective } from './directives/checkall/checkall.directive';
import { VectormapDirective } from './directives/vectormap/vectormap.directive';
import { NowDirective } from './directives/now/now.directive';
import { ScrollableDirective } from './directives/scrollable/scrollable.directive';
import { JqcloudDirective } from './directives/jqcloud/jqcloud.directive';

import { AppConfigService } from './appconfig/appconfig.service';
import { CookiesService } from './cookies/cookies.service';
import { ExLoaderService } from './exloader/exloader.service';
import { ClipboardService } from './clipboard/clipboard.service';

import { ScriptEditorComponent } from './components/scripteditor/scripteditor.component';
import { AiParametersEditorComponent } from './components/ai-parameters-editor/ai-parameters-editor.component';
import { HelpCardComponent } from './components/helpcard/helpcard.component';
import { AboutDialogComponent, AboutDialogContentComponent } from './components/about-dialog/about-dialog.component';
import { SectionHeaderComponent } from './components/section-header/section-header.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { LoadingOverlayComponent } from './components/loading-overlay/loading-overlay.component';
import { ImageUploadComponent } from './components/image-upload/image-upload.component';
import { SelectionCardComponent } from './components/selection-card/selection-card.component';
import { StatusBannerComponent, BannerActionDirective } from './components/status-banner/status-banner.component';
import { KendoInputDialogComponent } from './components/kendo-input-dialog/kendo-input-dialog.component';
import { DeleteOrgDialogComponent } from './components/delete-org-dialog/delete-org-dialog.component';
import { TeamsApprovalComponent } from './components/teams-approval/teams-approval.component';
import { TeamsMultiTenantApprovalComponent } from './components/teams-multitenant-approval/teams-multitenant-approval.component';
import { SlackApprovalComponent } from './components/slack-approval/slack-approval.component';
import { PsaCustomizationFormComponent } from './components/psa-customization-form/psa-customization-form.component';

import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";

import { DynamicFormsModule } from './dynamic-forms/dynamic-forms.module';
import { DataGridModule } from './components/data-grid/data-grid.module';

import {
  CloudRadialChatChatwidgetModule, CloudRadialChatChatwidgetComponent
} from '@cloudradial/crchat-chatwidget';

import { MessageTimePipe } from './pipes/message-time.pipe';
import { SanitizeHtmlPipe } from './pipes/sanitize-html.pipe';

export function initializeApp(
  appConfig: AppConfigService) {
  return () => {
    return appConfig.load();
  }
}

// https://angular.io/styleguide#!#04-10
@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    CloudRadialChatChatwidgetModule,
    TranslateModule,
    SectionHeaderComponent,
    PageHeaderComponent,
    ImageUploadComponent,
    StatusBannerComponent,
    BannerActionDirective,
    PsaCustomizationFormComponent,
    DynamicFormsModule,
    DataGridModule,
    AccordionModule.forRoot(),
    AlertModule.forRoot(),
    ButtonsModule.forRoot(),
    CarouselModule.forRoot(),
    CollapseModule.forRoot(),
    BsDatepickerModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    PaginationModule.forRoot(),
    ProgressbarModule.forRoot(),
    RatingModule.forRoot(),
    TabsModule.forRoot(),
    TimepickerModule.forRoot(),
    TooltipModule.forRoot(),
    PopoverModule.forRoot(),
    TypeaheadModule.forRoot(),
    ToastrModule.forRoot(),
    DialogsModule,
    KendoButtonsModule,
  ],
  providers: [
    ColorsService,
    NgxSpinnerService,
    ClipboardService,
    ExLoaderService,
    CookiesService,
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfigService],
      multi: true
    }
  ],
  declarations: [
    FlotDirective,
    SparklineDirective,
    EasypiechartDirective,
    CheckallDirective,
    VectormapDirective,
    NowDirective,
    ScrollableDirective,
    JqcloudDirective,
    ScriptEditorComponent,
    HelpCardComponent,
    AboutDialogComponent,
    AboutDialogContentComponent,
    LoadingOverlayComponent,
    SelectionCardComponent,
    KendoInputDialogComponent,
    DeleteOrgDialogComponent,
    TeamsApprovalComponent,
    TeamsMultiTenantApprovalComponent,
    SlackApprovalComponent,
    MessageTimePipe,
    SanitizeHtmlPipe,
    AiParametersEditorComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DynamicFormsModule,
    DataGridModule,
    TranslateModule,
    CloudRadialChatChatwidgetComponent,
    RouterModule,
    AccordionModule,
    AlertModule,
    ButtonsModule,
    CarouselModule,
    CollapseModule,
    BsDatepickerModule,
    BsDropdownModule,
    ModalModule,
    PaginationModule,
    ProgressbarModule,
    RatingModule,
    TabsModule,
    TimepickerModule,
    TooltipModule,
    PopoverModule,
    TypeaheadModule,
    ToastrModule,
    DialogsModule,
    KendoButtonsModule,
    FlotDirective,
    SparklineDirective,
    EasypiechartDirective,
    CheckallDirective,
    VectormapDirective,
    NowDirective,
    ScrollableDirective,
    JqcloudDirective,
    MessageTimePipe,
    SanitizeHtmlPipe,
    ScriptEditorComponent,
    HelpCardComponent,
    AboutDialogComponent,
    SectionHeaderComponent,
    PageHeaderComponent,
    LoadingOverlayComponent,
    ImageUploadComponent,
    SelectionCardComponent,
    StatusBannerComponent,
    BannerActionDirective,
    TeamsApprovalComponent,
    TeamsMultiTenantApprovalComponent,
    SlackApprovalComponent,
    PsaCustomizationFormComponent,
    AiParametersEditorComponent,
    NgxSpinnerModule
  ],
})

// https://github.com/ocombe/ng2-translate/issues/209
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
    };
  }
}
